import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { notifyQueryError, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CREATE_ATTACHMENT,
  DELETE_ATTACHMENT,
} from "utils/api/graphql/mutations/attachment";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { ATTACHMENTS } from "utils/api/graphql/queries/attachments";
import { downloadDocument } from "utils/helpers/files";
import { mountOnce } from "./mountOnce";

export const useAttachmentField = () => {
  const [createAttachment] = useMutation(CREATE_ATTACHMENT);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);
  const client = useApolloClient();
  const { t } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = t("API", { returnObjects: true });

  const downloadStoredAttachment = (id) => {
    if (!id) return;
    downloadSingleDocument({
      variables: { where: { id } },
    }).then((result) => {
      const data = result && result.data;
      const payload = data && data.downloadSingleDocument;
      const document = payload && payload.document;
      if (!document) return;
      downloadDocument(document);
    });
  };

  const mount = ({ el, attachmentType, projectId, subscriptionId }) => {
    if (!attachmentType || !projectId) return;

    const scope =
      el.closest("[data-attachment-scope]") ||
      (el.closest("label") && el.closest("label").parentElement) ||
      el.parentElement;
    const listEl = scope && scope.querySelector("[data-attachment-list]");
    const templateEl = listEl && listEl.querySelector("[data-attachment-item]");

    const renderFiles = (files) => {
      if (!listEl || !templateEl) return;
      const existing = listEl.querySelectorAll("[data-attachment-rendered]");
      for (const e of existing) e.remove();

      for (const att of files) {
        const clone = templateEl.cloneNode(true);
        clone.removeAttribute("data-attachment-item");
        clone.setAttribute("data-attachment-rendered", "true");
        clone.style.display = "";

        const nameEl = clone.querySelector("[data-attachment-name]");
        if (nameEl) {
          nameEl.textContent = att.name || att.filename || att.id;
          const link = nameEl.closest("a");
          if (link) link.removeAttribute("href");
          nameEl.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            downloadStoredAttachment(att.id);
          });
        }

        const removeEl = clone.querySelector(
          "[data-action='removeAttachment']",
        );
        if (removeEl) {
          removeEl.addEventListener("click", (e) => {
            e.stopPropagation();
            e.preventDefault();
            deleteAttachment({ variables: { where: { id: att.id } } }).then(
              () => loadFiles(),
            );
          });
        }

        listEl.appendChild(clone);
      }
    };

    const loadFiles = () =>
      client
        .query({
          query: ATTACHMENTS,
          variables: {
            where: { type: attachmentType, project: { id: projectId } },
          },
          fetchPolicy: "network-only",
        })
        .then(({ data: result }) =>
          renderFiles(result?.attachments?.data || []),
        )
        .catch((error) => notifyQueryError(error, API_ERRORS));

    const onChange = () => {
      const file = el.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        createAttachment({
          variables: {
            data: {
              id: "att_" + Date.now(),
              name: file.name,
              content: base64,
              contentType: file.type,
              type: attachmentType,
              project: { id: projectId },
              ...(subscriptionId
                ? { subscription: { id: subscriptionId } }
                : {}),
            },
          },
        }).then(() => {
          el.value = "";
          loadFiles();
        });
      };
      reader.readAsDataURL(file);
    };

    mountOnce(
      el,
      `${attachmentType}:${projectId}:${subscriptionId || ""}`,
      () => {
        el.addEventListener("change", onChange);
        loadFiles();
        return () => el.removeEventListener("change", onChange);
      },
    );
  };

  return mount;
};
